import { trackEvent } from "../utils/track-event";
import type { TrackingEvent, TrackingEventResponse } from "../utils/types";
import { SEGMENT_ENGAGEMENT_TIMEOUT } from "src/segment";
import type { SingleProduct, ProductVariation } from "src/types/graphql.d";

type ProductEngagedEventProps = {
  product: SingleProduct;
  resultId?: string;
};

type ProductEngagedAnalyticsProps = {
  engagement_type: string;
  brand: string;
  brand_id: string;
  category: unknown;
  category_id: unknown;
  currency?: string;
  image_url?: string;
  labels: string[];
  name: string;
  price: number;
  product_discount?: number | null;
  product_id: string;
  product_kids_gender?: unknown;
  product_shop_type?: unknown;
  regular_price: number;
  sizes_available?: unknown;
  url: string;
  gender?: string | null;
  product_variation?: string | null;
  gate_level: unknown;
  result_id?: string;
};

export type ProductEngagedEvent = TrackingEvent<
  "Product Engaged",
  ProductEngagedAnalyticsProps
>;

const getAvailableSizes = (variations?: ProductVariation[]) =>
  (variations ?? []).filter((v) => v.stock_quantity > 0).map((v) => v.size);

export async function trackProductEngagedEvent({
  product,
  resultId,
}: ProductEngagedEventProps): TrackingEventResponse {
  return await trackEvent("Product Engaged", {
    props: {
      engagement_type: `${SEGMENT_ENGAGEMENT_TIMEOUT} seconds`,
      brand: product.brand.name,
      brand_id: product.brand.id,
      category: product?.translated_category_names || [],
      category_id: product?.translated_category_ids || [],
      currency: product?.currency?.code,
      image_url: product.images?.[0],
      labels: (product.labels || []).map(({ value }) => value),
      name: product.name,
      price: product.price,
      product_discount: product.product_discount,
      product_id: product.id,
      product_kids_gender: product.product_shop_type?.includes("kids")
        ? product.product_kids_gender
        : undefined,
      product_shop_type: product.product_shop_type,
      regular_price: product.regular_price,
      sizes_available: product.variations
        ? getAvailableSizes(product.variations)
        : undefined,
      url: `product/${product.slug}`,
      gender: product.gender,
      product_variation: product.variations?.[0]?.id,
      gate_level: product.gate?.level || null,
      result_id: resultId,
    },
    withRequestSource: true,
  });
}
